import { useEffect, useState } from 'react'
import { UserPlus, Edit2, KeyRound, Trash2, Check, X as XIcon, Shield, ShieldCheck, LogIn, Eye, Pencil } from 'lucide-react'
import toast from 'react-hot-toast'
import { api } from '@/lib/api'
import { useAuth, cn } from '@/lib/auth'
import { entrarComoUsuario } from '@/lib/impersonate'

interface Usuario {
  id: number
  nome: string
  email: string
  perfil: string
  ativo: boolean
  empresa_id: number | null
  custo_hora?: number
}

interface PerfilOpt { value: string; label: string }

const PERFIL_COLORS: Record<string, string> = {
  super_admin: 'bg-amber-100 text-amber-800',
  admin: 'bg-brand-100 text-brand-800',
  gerente: 'bg-indigo-100 text-indigo-800',
  financeiro: 'bg-emerald-100 text-emerald-800',
  operador: 'bg-gray-100 text-gray-700',
}


export default function UsuariosPage() {
  const { user: currentUser, empresa } = useAuth()
  const [usuarios, setUsuarios] = useState<Usuario[]>([])
  const [loading, setLoading] = useState(true)
  const [modalOpen, setModalOpen] = useState(false)
  const [editando, setEditando] = useState<Usuario | null>(null)
  const [resetPwFor, setResetPwFor] = useState<Usuario | null>(null)
  const [entrarFor, setEntrarFor] = useState<Usuario | null>(null)
  const [perfilOpts, setPerfilOpts] = useState<PerfilOpt[]>([])

  const canManage = (currentUser as any)?.nivel === 'admin' || currentUser?.perfil === 'admin' || currentUser?.perfil === 'super_admin'

  const carregar = async () => {
    setLoading(true)
    try {
      const { data } = await api.get('/auth/usuarios')
      setUsuarios(data)
    } catch {
      // interceptor mostra toast
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { carregar() }, [])

  useEffect(() => {
    api.get('/auth/perfis')
      .then(({ data }) => setPerfilOpts(data.filter((p: any) => p.ativo).map((p: any) => ({ value: p.slug, label: p.nome }))))
      .catch(() => {})
  }, [])

  const abrirNovo = () => { setEditando(null); setModalOpen(true) }
  const abrirEditar = (u: Usuario) => { setEditando(u); setModalOpen(true) }

  const toggleAtivo = async (u: Usuario) => {
    if (u.id === currentUser?.id) {
      toast.error('Você não pode desativar seu próprio usuário')
      return
    }
    try {
      await api.put(`/auth/usuarios/${u.id}`, { ativo: !u.ativo })
      toast.success(u.ativo ? 'Usuário desativado' : 'Usuário ativado')
      carregar()
    } catch {}
  }

  if (!canManage) {
    return (
      <div className="p-8 max-w-xl">
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-amber-800 text-sm">
          <Shield className="inline mr-2" size={16} />
          Apenas administradores podem acessar a gestão de usuários.
        </div>
      </div>
    )
  }

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Usuários</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            {empresa?.nome_fantasia
              ? `Gestão de usuários de ${empresa.nome_fantasia}`
              : 'Visão global de todos os usuários'}
            {empresa?.max_usuarios && ` • ${usuarios.filter(u => u.ativo).length}/${empresa.max_usuarios} ativos`}
          </p>
        </div>
        <button
          onClick={abrirNovo}
          className="inline-flex items-center gap-2 px-4 py-2 bg-brand-600 text-white text-sm font-medium rounded-md hover:bg-brand-700"
        >
          <UserPlus size={16} /> Novo usuário
        </button>
      </div>

      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        <table className="w-full">
          <thead className="bg-gray-50 border-b">
            <tr>
              <th className="text-left text-xs font-semibold text-gray-600 uppercase px-4 py-3">Nome</th>
              <th className="text-left text-xs font-semibold text-gray-600 uppercase px-4 py-3">E-mail</th>
              <th className="text-left text-xs font-semibold text-gray-600 uppercase px-4 py-3">Perfil</th>
              <th className="text-left text-xs font-semibold text-gray-600 uppercase px-4 py-3">Status</th>
              <th className="text-right text-xs font-semibold text-gray-600 uppercase px-4 py-3">Ações</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {loading && (
              <tr><td colSpan={5} className="text-center py-8 text-gray-400 text-sm">Carregando...</td></tr>
            )}
            {!loading && usuarios.length === 0 && (
              <tr><td colSpan={5} className="text-center py-8 text-gray-400 text-sm">Nenhum usuário cadastrado</td></tr>
            )}
            {!loading && usuarios.map(u => (
              <tr key={u.id}
                onClick={() => abrirEditar(u)}
                className={cn('hover:bg-violet-50 cursor-pointer', !u.ativo && 'bg-gray-50 opacity-60')}>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    <div className="w-7 h-7 rounded-full bg-brand-600 text-white flex items-center justify-center text-xs font-semibold">
                      {u.nome.charAt(0).toUpperCase()}
                    </div>
                    <span className="text-sm font-medium text-gray-900">{u.nome}</span>
                    {u.id === currentUser?.id && (
                      <span className="text-[10px] bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded">Você</span>
                    )}
                  </div>
                </td>
                <td className="px-4 py-3 text-sm text-gray-700">{u.email}</td>
                <td className="px-4 py-3">
                  <span className={cn(
                    'inline-flex items-center gap-1 text-[11px] font-semibold uppercase px-2 py-0.5 rounded',
                    PERFIL_COLORS[u.perfil] || 'bg-gray-100 text-gray-700'
                  )}>
                    {u.perfil === 'super_admin' && <ShieldCheck size={11} />}
                    {u.perfil.replace('_', ' ')}
                  </span>
                </td>
                <td className="px-4 py-3">
                  {u.ativo ? (
                    <span className="inline-flex items-center gap-1 text-xs text-emerald-700">
                      <Check size={12} /> Ativo
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-xs text-gray-500">
                      <XIcon size={12} /> Inativo
                    </span>
                  )}
                </td>
                <td className="px-4 py-3" onClick={e => e.stopPropagation()}>
                  <div className="flex items-center justify-end gap-1">
                    {u.id !== currentUser?.id && (
                      <button onClick={() => setEntrarFor(u)} title="Entrar no perfil deste usuário"
                        className="p-1.5 rounded hover:bg-gray-100 text-gray-500 hover:text-indigo-600">
                        <LogIn size={14} />
                      </button>
                    )}
                    <button onClick={() => abrirEditar(u)} title="Editar"
                      className="p-1.5 rounded hover:bg-gray-100 text-gray-500 hover:text-brand-600">
                      <Edit2 size={14} />
                    </button>
                    <button onClick={() => setResetPwFor(u)} title="Resetar senha"
                      className="p-1.5 rounded hover:bg-gray-100 text-gray-500 hover:text-amber-600">
                      <KeyRound size={14} />
                    </button>
                    <button onClick={() => toggleAtivo(u)}
                      title={u.ativo ? 'Desativar' : 'Ativar'}
                      disabled={u.id === currentUser?.id}
                      className="p-1.5 rounded hover:bg-gray-100 text-gray-500 hover:text-red-600 disabled:opacity-30 disabled:cursor-not-allowed">
                      <Trash2 size={14} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <UsuarioModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        usuario={editando}
        onSaved={carregar}
        isSuperAdmin={currentUser?.perfil === 'super_admin'}
        perfilOpts={perfilOpts}
      />
      <ResetPasswordModal
        usuario={resetPwFor}
        onClose={() => setResetPwFor(null)}
      />
      <EntrarComoModal
        usuario={entrarFor}
        onClose={() => setEntrarFor(null)}
      />
    </div>
  )
}


/* ============================================================
   Modal: entrar no perfil do usuário (impersonação)
============================================================ */
function EntrarComoModal({ usuario, onClose }: { usuario: Usuario | null; onClose: () => void }) {
  const [loading, setLoading] = useState<'visualizacao' | 'edicao' | null>(null)
  if (!usuario) return null

  const entrar = async (modo: 'visualizacao' | 'edicao') => {
    setLoading(modo)
    try {
      await entrarComoUsuario(usuario.id, modo)  // recarrega a página já como o usuário
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || 'Erro ao entrar no perfil')
      setLoading(null)
    }
  }

  return (
    <div className="fixed inset-0 bg-black/50 z-[60] flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b">
          <h2 className="font-semibold text-gray-900 flex items-center gap-2"><LogIn size={18} /> Entrar como usuário</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700 p-1"><XIcon size={18} /></button>
        </div>
        <div className="p-5 space-y-4">
          <p className="text-sm text-gray-600">
            Você vai navegar o sistema como <b>{usuario.nome}</b> ({(usuario.perfil || '').replace('_', ' ')}),
            vendo exatamente o que ele vê. Escolha o modo:
          </p>
          <div className="grid grid-cols-2 gap-3">
            <button onClick={() => entrar('visualizacao')} disabled={!!loading}
              className="flex flex-col items-center gap-1.5 rounded-lg border-2 border-amber-200 bg-amber-50 hover:bg-amber-100 p-4 text-amber-900 disabled:opacity-50">
              <Eye size={22} />
              <span className="font-semibold text-sm">Visualização</span>
              <span className="text-[11px] text-amber-700 text-center leading-tight">Só consulta — não grava nada</span>
            </button>
            <button onClick={() => entrar('edicao')} disabled={!!loading}
              className="flex flex-col items-center gap-1.5 rounded-lg border-2 border-brand-200 bg-brand-50 hover:bg-brand-100 p-4 text-brand-800 disabled:opacity-50">
              <Pencil size={22} />
              <span className="font-semibold text-sm">Edição</span>
              <span className="text-[11px] text-brand-600 text-center leading-tight">Pode editar como o usuário</span>
            </button>
          </div>
          <p className="text-xs text-gray-400">Você poderá voltar à sua conta a qualquer momento pela faixa no topo.</p>
        </div>
      </div>
    </div>
  )
}


/* ============================================================
   Modal: criar/editar usuário
============================================================ */
interface UsuarioModalProps {
  open: boolean
  onClose: () => void
  usuario: Usuario | null
  onSaved: () => void
  isSuperAdmin: boolean
  perfilOpts: PerfilOpt[]
}

function UsuarioModal({ open, onClose, usuario, onSaved, isSuperAdmin, perfilOpts }: UsuarioModalProps) {
  const [nome, setNome] = useState('')
  const [email, setEmail] = useState('')
  const [senha, setSenha] = useState('')
  const [perfil, setPerfil] = useState('operador')
  const [custoHora, setCustoHora] = useState('0')
  const [ehVendedor, setEhVendedor] = useState(false)
  const [ehMecanico, setEhMecanico] = useState(false)
  const [loading, setLoading] = useState(false)
  const [filiais, setFiliais] = useState<{ id: number; nome: string }[]>([])
  const [filialSel, setFilialSel] = useState<number[]>([])

  useEffect(() => {
    if (open) {
      setNome(usuario?.nome || '')
      setEmail(usuario?.email || '')
      setPerfil(usuario?.perfil || 'operador')
      setCustoHora(String(usuario?.custo_hora || 0))
      setEhVendedor(!!(usuario as any)?.eh_vendedor)
      setEhMecanico(!!(usuario as any)?.eh_mecanico)
      setSenha('')
      setLoading(false)
      // Lojas (filiais) da empresa + as que o usuário já acessa
      api.get('/filiais').then(({ data }) =>
        setFiliais((data || []).map((f: any) => ({ id: f.id, nome: f.nome_fantasia || f.razao_social || f.codigo })))
      ).catch(() => setFiliais([]))
      if (usuario?.id) {
        api.get(`/auth/usuarios/${usuario.id}/filiais`)
          .then(({ data }) => setFilialSel(data.filial_ids || [])).catch(() => setFilialSel([]))
      } else {
        setFilialSel([])
      }
    }
  }, [open, usuario])

  const toggleFilial = (id: number) =>
    setFilialSel(s => s.includes(id) ? s.filter(x => x !== id) : [...s, id])

  if (!open) return null

  const isEdicao = !!usuario

  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    try {
      const custo = parseFloat(custoHora) || 0
      let uid = usuario?.id
      if (isEdicao && usuario) {
        await api.put(`/auth/usuarios/${usuario.id}`, { nome, email, perfil, custo_hora: custo, eh_vendedor: ehVendedor, eh_mecanico: ehMecanico })
        toast.success('Usuário atualizado')
      } else {
        if (senha.length < 6) { toast.error('Senha precisa ter no mínimo 6 caracteres'); setLoading(false); return }
        const { data } = await api.post('/auth/usuarios', { nome, email, senha, perfil, custo_hora: custo, eh_vendedor: ehVendedor, eh_mecanico: ehMecanico })
        uid = data?.id
        toast.success('Usuário criado')
      }
      // Salva as lojas (filiais) que esse usuário acessa
      if (uid && filiais.length) {
        await api.put(`/auth/usuarios/${uid}/filiais`, { filial_ids: filialSel })
      }
      onSaved()
      onClose()
    } catch {
    } finally {
      setLoading(false)
    }
  }

  const base: PerfilOpt[] = perfilOpts.length ? perfilOpts : [
    { value: 'admin', label: 'Administrador' },
    { value: 'gerente', label: 'Gerente' },
    { value: 'financeiro', label: 'Financeiro' },
    { value: 'operador', label: 'Operador' },
  ]
  const perfisDisponiveis = isSuperAdmin
    ? [...base, { value: 'super_admin', label: 'Super Admin' }]
    : base

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-5 py-4 border-b shrink-0">
          <h2 className="font-semibold text-gray-900">
            {isEdicao ? 'Editar usuário' : 'Novo usuário'}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700 p-1">
            <XIcon size={18} />
          </button>
        </div>
        <form onSubmit={submit} className="flex flex-col min-h-0 flex-1">
          <div className="p-5 space-y-4 overflow-y-auto">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Nome</label>
            <input type="text" required autoFocus value={nome} onChange={e => setNome(e.target.value)}
              className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-brand-500 focus:border-brand-500 text-sm"
              placeholder="Nome completo" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">E-mail</label>
            <input type="email" required value={email} onChange={e => setEmail(e.target.value)}
              className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-brand-500 focus:border-brand-500 text-sm"
              placeholder="email@empresa.com" />
          </div>
          {!isEdicao && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Senha inicial</label>
              <input type="password" required minLength={6} value={senha} onChange={e => setSenha(e.target.value)}
                className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-brand-500 focus:border-brand-500 text-sm"
                placeholder="Mínimo 6 caracteres" />
              <p className="text-xs text-gray-500 mt-1">
                O usuário poderá trocar a senha depois no próprio menu.
              </p>
            </div>
          )}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Perfil</label>
            <select value={perfil} onChange={e => setPerfil(e.target.value)}
              className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-brand-500 focus:border-brand-500 text-sm bg-white">
              {perfisDisponiveis.map(p => (
                <option key={p.value} value={p.value}>{p.label}</option>
              ))}
            </select>
            <p className="text-xs text-gray-500 mt-1">
              <strong>Admin</strong>: acesso total • <strong>Gerente</strong>: vendas, estoque, relatórios • <strong>Financeiro</strong>: financeiro + relatórios • <strong>Operador</strong>: PDV + vendas
            </p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Custo/hora (MOD) <span className="text-gray-400 font-normal">R$</span>
            </label>
            <input type="number" step="0.01" value={custoHora} onChange={e => setCustoHora(e.target.value)}
              className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-brand-500 focus:border-brand-500 text-sm"
              placeholder="0.00" />
            <p className="text-xs text-gray-500 mt-1">
              Usado no custeio de produção: quando este usuário conclui uma etapa de OP, o tempo × este valor entra no custo de mão de obra direta.
            </p>
          </div>
          <div>
            <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
              <input type="checkbox" checked={ehVendedor} onChange={e => setEhVendedor(e.target.checked)} />
              É vendedor
            </label>
            <p className="text-xs text-gray-500 mt-1">Marque pra este usuário poder ser amarrado como vendedor de clientes e aparecer nos relatórios por vendedor.</p>
          </div>
          <div>
            <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
              <input type="checkbox" checked={ehMecanico} onChange={e => setEhMecanico(e.target.checked)} />
              É mecânico / técnico
            </label>
            <p className="text-xs text-gray-500 mt-1">Marque pra este usuário aparecer no campo Mecânico/Técnico da Ordem de Serviço.</p>
          </div>
          {filiais.length > 0 && perfil !== 'admin' && perfil !== 'super_admin' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Lojas que este usuário acessa</label>
              <div className="border rounded-md divide-y max-h-44 overflow-auto">
                {filiais.map(f => (
                  <label key={f.id} className="flex items-center gap-2 px-3 py-2 text-sm text-gray-700 cursor-pointer hover:bg-gray-50">
                    <input type="checkbox" checked={filialSel.includes(f.id)} onChange={() => toggleFilial(f.id)} />
                    {f.nome}
                  </label>
                ))}
              </div>
              <p className="text-xs text-gray-500 mt-1">
                {filialSel.length === 0
                  ? 'Nenhuma marcada = acessa TODAS as lojas.'
                  : filialSel.length === 1
                    ? 'Entra direto nesta loja (sem troca no topo).'
                    : `Pode alternar entre as ${filialSel.length} lojas marcadas.`}
              </p>
            </div>
          )}
          </div>
          <div className="flex gap-2 px-5 py-4 border-t shrink-0">
            <button type="button" onClick={onClose} disabled={loading}
              className="flex-1 px-4 py-2 border rounded-md text-sm font-medium hover:bg-gray-50 disabled:opacity-50">
              Cancelar
            </button>
            <button type="submit" disabled={loading}
              className="flex-1 px-4 py-2 bg-brand-600 text-white rounded-md text-sm font-medium hover:bg-brand-700 disabled:opacity-50">
              {loading ? 'Salvando...' : (isEdicao ? 'Salvar' : 'Criar usuário')}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}


/* ============================================================
   Modal: resetar senha de outro usuário
============================================================ */
interface ResetPasswordModalProps {
  usuario: Usuario | null
  onClose: () => void
}

function ResetPasswordModal({ usuario, onClose }: ResetPasswordModalProps) {
  const [senha, setSenha] = useState('')
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (usuario) { setSenha(''); setLoading(false) }
  }, [usuario])

  if (!usuario) return null

  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (senha.length < 6) { toast.error('Senha precisa ter no mínimo 6 caracteres'); return }
    setLoading(true)
    try {
      await api.post(`/auth/usuarios/${usuario.id}/reset-senha`, { senha_nova: senha })
      toast.success(`Senha de ${usuario.email} resetada`)
      onClose()
    } catch {
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
        <div className="flex items-center justify-between px-5 py-4 border-b">
          <div className="flex items-center gap-2">
            <KeyRound size={18} className="text-amber-600" />
            <h2 className="font-semibold text-gray-900">Resetar senha</h2>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700 p-1">
            <XIcon size={18} />
          </button>
        </div>
        <form onSubmit={submit} className="p-5 space-y-4">
          <div className="bg-amber-50 border border-amber-200 text-amber-900 text-sm rounded-md p-3">
            Você está resetando a senha de <strong>{usuario.nome}</strong> ({usuario.email}).
            Informe a nova senha abaixo e comunique ao usuário.
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Nova senha</label>
            <input type="password" required minLength={6} autoFocus value={senha} onChange={e => setSenha(e.target.value)}
              className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-brand-500 focus:border-brand-500 text-sm"
              placeholder="Mínimo 6 caracteres" />
          </div>
          <div className="flex gap-2 pt-2">
            <button type="button" onClick={onClose} disabled={loading}
              className="flex-1 px-4 py-2 border rounded-md text-sm font-medium hover:bg-gray-50 disabled:opacity-50">
              Cancelar
            </button>
            <button type="submit" disabled={loading}
              className="flex-1 px-4 py-2 bg-amber-600 text-white rounded-md text-sm font-medium hover:bg-amber-700 disabled:opacity-50">
              {loading ? 'Resetando...' : 'Resetar senha'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
